import React, { useEffect, useMemo, useState } from "react";
import { listarMetodos } from "../../../../services/pagoMetodosService";
import empresasService from "../../../../services/empresasService";
import { buscarMatriculas } from "../../../../services/matriculasService";
import pagoEmpresaService, { obtenerSiguienteReferencia } from "../../../../services/pagoEmpresaService";
import { buildCompanyPrintHtml, printHtml } from "./PagoDetailPanel";
import { showToast } from "../../../../utils/showToast";

const currency = (v) => `$${Number(v || 0).toFixed(2)}`;

// ── Wrapper modal ──────────────────────────────────────────────
// Se exportan ModalWrapper/SearchableSelect/MatriculasPicker para que
// CuentaPorCobrarModal.jsx los reutilice sin duplicar el patrón visual.
export const ModalWrapper = ({ open, title, subtitle, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <div>
              <h5 className="modal-title mb-0">{title}</h5>
              {subtitle && <p className="management-eyebrow mb-0 mt-1">{subtitle}</p>}
            </div>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// ── Dropdown searchable reutilizable ───────────────────────────
export const SearchableSelect = ({ label, value, displayValue, options, searchTerm, onSearch, onSelect, placeholder = "Buscar..." }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-3 position-relative">
      <label className="form-label small fw-semibold">{label}</label>
      <input
        type="text"
        className="form-control"
        placeholder={placeholder}
        value={open ? searchTerm : (displayValue || searchTerm)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onChange={(e) => onSearch(e.target.value)}
        autoComplete="off"
      />
      {open && options.length > 0 && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050, maxHeight: 200, overflowY: "auto" }}>
          {options.map((opt) => (
            <li
              key={opt.id}
              className={`list-group-item list-group-item-action py-2 small${String(opt.id) === String(value) ? " active" : ""}`}
              onMouseDown={() => { onSelect(opt); setOpen(false); }}
              style={{ cursor: "pointer" }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
      {open && options.length === 0 && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050 }}>
          <li className="list-group-item py-2 small text-muted">Sin resultados</li>
        </ul>
      )}
    </div>
  );
};

// ── Lista de matrículas con checkboxes ─────────────────────────
export const MatriculasPicker = ({ seleccionados, onToggle, onToggleAll, resultados, buscando, query, onQuery }) => {
  const allIds = resultados.map((m) => m.id);
  const todosSeleccionados = allIds.length > 0 && allIds.every((id) => seleccionados.has(id));

  return (
    <div>
      <label className="form-label small fw-semibold">Buscar matrículas</label>
      <input
        className="form-control form-control-sm mb-2"
        placeholder="Nombre, apellido o código de matrícula..."
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      {buscando && <p className="text-muted small mb-1">Buscando...</p>}
      {!buscando && resultados.length === 0 && query.length >= 2 && (
        <p className="text-muted small mb-1">Sin resultados para "{query}"</p>
      )}
      {resultados.length > 0 && (
        <div className="border rounded" style={{ maxHeight: 220, overflowY: "auto" }}>
          {allIds.length > 1 && (
            <label className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom small fw-semibold" style={{ cursor: "pointer" }}>
              <input type="checkbox" className="form-check-input mt-0" checked={todosSeleccionados} onChange={onToggleAll} />
              Seleccionar todos ({resultados.length})
            </label>
          )}
          {resultados.map((m) => (
            <label
              key={m.id}
              className="d-flex align-items-center gap-2 px-3 py-2 border-bottom small"
              style={{ cursor: "pointer", backgroundColor: seleccionados.has(m.id) ? "rgba(99,102,241,0.05)" : undefined }}
            >
              <input
                type="checkbox"
                className="form-check-input mt-0 flex-shrink-0"
                checked={seleccionados.has(m.id)}
                onChange={() => onToggle(m.id)}
              />
              <div className="flex-fill">
                <span className="fw-semibold">{m.estudiante_nombres} {m.estudiante_apellidos}</span>
                <span className="text-muted ms-2">{m.codigo_matricula || `#${m.id}`}</span>
              </div>
              <div className="text-end flex-shrink-0">
                <span className="fw-semibold">{currency(m.saldo)}</span>
                <span className={`badge ms-2 bg-${m.estado_pago === "pagado" ? "success" : m.estado_pago === "parcial" ? "warning text-dark" : "secondary"}`}>
                  {m.estado_pago}
                </span>
              </div>
            </label>
          ))}
        </div>
      )}
      {seleccionados.size > 0 && (
        <p className="text-muted small mt-1 mb-0">
          {seleccionados.size} matrícula{seleccionados.size > 1 ? "s" : ""} seleccionada{seleccionados.size > 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// MODO: CREAR nuevo pago empresarial
// ══════════════════════════════════════════════════════════════
function CrearPagoEmpresa({ onClose, onSuccess }) {
  const [empresas, setEmpresas] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [empresaSearch, setEmpresaSearch] = useState("");
  const [metodoSearch, setMetodoSearch] = useState("");
  const [empresaId, setEmpresaId] = useState(null);
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [metodoId, setMetodoId] = useState(null);
  const [metodoNombre, setMetodoNombre] = useState("");
  const [metodoPrefijo, setMetodoPrefijo] = useState("");
  const [montoTotal, setMontoTotal] = useState("");
  const [referencia, setReferencia] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [esPorCobrar, setEsPorCobrar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  useEffect(() => {
    empresasService.listar().then((data) => setEmpresas(Array.isArray(data) ? data : [])).catch(() => {});
    listarMetodos().then((data) => setMetodos(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Buscar matrículas con debounce
  useEffect(() => {
    if (query.trim().length < 2) { setResultados([]); return; }
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const data = await buscarMatriculas({ search: query, estado_pago: "pendiente,parcial", limit: 30 });
        setResultados(Array.isArray(data) ? data : (data?.items || []));
      } catch { setResultados([]); }
      finally { setBuscando(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const empresasSugeridas = useMemo(() => {
    const t = empresaSearch.trim().toLowerCase();
    return (t ? empresas.filter((e) => e.nombre.toLowerCase().includes(t)) : empresas)
      .map((e) => ({ id: e.id, label: `${e.nombre}${e.ruc ? ` — ${e.ruc}` : ""}` }));
  }, [empresas, empresaSearch]);

  const metodosSugeridos = useMemo(() => {
    const t = metodoSearch.trim().toLowerCase();
    return (t ? metodos.filter((m) => m.nombre.toLowerCase().includes(t)) : metodos)
      .map((m) => ({ id: m.id, label: m.nombre, prefijo: m.prefijo_referencia }));
  }, [metodos, metodoSearch]);

  const handleSelectMetodo = async (opt) => {
    setMetodoId(opt.id);
    setMetodoNombre(opt.label);
    setMetodoPrefijo(opt.prefijo || "");
    setMetodoSearch("");
    if (opt.prefijo) {
      try {
        const data = await obtenerSiguienteReferencia(opt.prefijo);
        setReferencia(data.referencia || "");
      } catch { /* si falla, el usuario puede ingresar manualmente */ }
    }
  };

  const toggleMatricula = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    const allIds = resultados.map((m) => m.id);
    const todos = allIds.every((id) => seleccionados.has(id));
    setSeleccionados(todos ? new Set() : new Set(allIds));
  };

  const sumaSeleccionados = resultados
    .filter((m) => seleccionados.has(m.id))
    .reduce((acc, m) => acc + Number(m.saldo || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!empresaId) return showToast({ type: "warning", message: "Selecciona una empresa" });
    if (!metodoId) return showToast({ type: "warning", message: "Selecciona un método de pago" });
    if (!montoTotal || Number(montoTotal) <= 0) return showToast({ type: "warning", message: "Ingresa un monto válido" });

    setSubmitting(true);
    try {
      const res = await pagoEmpresaService.crear({
        empresa_id: empresaId,
        matricula_ids: [...seleccionados],
        monto_total: Number(montoTotal),
        metodo_id: metodoId,
        referencia_externa: referencia.trim() || undefined,
        observaciones: observaciones.trim() || undefined,
        estado: esPorCobrar ? "por_cobrar" : undefined,
      });
      showToast({ type: "success", message: "Pago empresarial registrado correctamente" });
      onSuccess?.(res);
      setResultado(res);
    } catch (err) {
      showToast({ type: "danger", message: err?.response?.data?.message || err.message || "Error al registrar pago" });
    } finally {
      setSubmitting(false);
    }
  };

  if (resultado) {
    return (
      <>
        <div className="modal-body pt-3 text-center">
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "2.5rem" }} />
          <h6 className="mt-3 mb-1">
            {resultado.estado === "por_cobrar" ? "Compromiso registrado" : "Pago registrado"}
          </h6>
          <p className="text-muted small mb-3">
            {empresaNombre} — {currency(resultado.monto_total)}
            {resultado.estado === "por_cobrar" && (
              <><br /><span className="badge bg-warning text-dark mt-1">Por cobrar — pendiente de confirmación</span></>
            )}
            {resultado.estado !== "por_cobrar" && resultado.saldo_favor > 0 && (
              <><br /><strong className="text-success">Saldo a favor: {currency(resultado.saldo_favor)}</strong></>
            )}
          </p>
          <p className="text-muted small">
            {resultado.estado === "por_cobrar"
              ? `${resultado.matriculas_vinculadas || 0} matrícula(s) vinculadas. Cuando la empresa pague, confirma el registro para aplicar el crédito.`
              : resultado.pagos?.length > 0
                ? `${resultado.pagos.length} matrícula(s) cubiertas.`
                : "Sin matrículas aplicadas aún — usa el saldo a favor cuando las matrículas estén creadas."}
          </p>
        </div>
        <div className="modal-footer border-0 justify-content-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={printLoading}
            onClick={async () => {
              setPrintLoading(true);
              try {
                const data = await pagoEmpresaService.obtenerComprobante(resultado.pago_empresa_id);
                printHtml(buildCompanyPrintHtml(data, "80mm"));
              } catch { showToast({ type: "danger", message: "No se pudo obtener el comprobante" }); }
              finally { setPrintLoading(false); }
            }}
          >
            <i className="bi bi-printer me-1" />
            {printLoading ? "Generando..." : "Imprimir comprobante"}
          </button>
          <button type="button" className="btn management-btn-primary" onClick={onClose}>
            Listo
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body pt-3">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <SearchableSelect
              label="Empresa *"
              value={empresaId}
              displayValue={empresaNombre}
              options={empresasSugeridas}
              searchTerm={empresaSearch}
              onSearch={(v) => { setEmpresaSearch(v); if (!v) { setEmpresaId(null); setEmpresaNombre(""); } }}
              onSelect={(opt) => { setEmpresaId(opt.id); setEmpresaNombre(opt.label); setEmpresaSearch(""); }}
              placeholder="Buscar empresa..."
            />
          </div>
          <div className="col-12 col-md-6">
            <SearchableSelect
              label="Método de pago *"
              value={metodoId}
              displayValue={metodoNombre}
              options={metodosSugeridos}
              searchTerm={metodoSearch}
              onSearch={(v) => { setMetodoSearch(v); if (!v) { setMetodoId(null); setMetodoNombre(""); } }}
              onSelect={handleSelectMetodo}
              placeholder="Seleccionar método..."
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small fw-semibold">Monto total *</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className="form-control"
                min="0.01"
                step="0.01"
                value={montoTotal}
                onChange={(e) => setMontoTotal(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {seleccionados.size > 0 && (
              <div className="form-text">
                Suma de saldos seleccionados: <strong>{currency(sumaSeleccionados)}</strong>
                {Number(montoTotal) > 0 && Number(montoTotal) >= sumaSeleccionados && (
                  <span className="ms-2 text-success fw-semibold">
                    Saldo a favor: {currency(Number(montoTotal) - sumaSeleccionados)}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small fw-semibold">Referencia</label>
            <input
              type="text"
              className="form-control"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder={metodoPrefijo ? `${metodoPrefijo}XXXXXXX` : "Selecciona un método primero"}
            />
            {referencia && (
              <div className="form-text">Puedes editarla o dejarla como fue generada.</div>
            )}
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">Observaciones</label>
            <textarea
              className="form-control"
              rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Ej: Pago grupal 5 nivel 1 + 6 nivel 2..."
            />
          </div>
          <div className="col-12">
            <hr className="my-1" />
            <p className="management-eyebrow mb-2">Matrículas a cubrir (opcional)</p>
            <p className="text-muted small mb-2">
              Puedes dejar esto vacío si las matrículas aún no existen — quedarán como saldo a favor para aplicar después.
            </p>
            <MatriculasPicker
              seleccionados={seleccionados}
              onToggle={toggleMatricula}
              onToggleAll={toggleAll}
              resultados={resultados}
              buscando={buscando}
              query={query}
              onQuery={setQuery}
            />
          </div>
          <div className="col-12 mt-1">
            <div className="form-check form-switch d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="chkPorCobrar"
                checked={esPorCobrar}
                onChange={(e) => setEsPorCobrar(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <label className="form-check-label small" htmlFor="chkPorCobrar">
                Registrar como <strong>por cobrar</strong> — la empresa confirma pero pagará después
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </button>
        <button type="submit" className="btn management-btn-primary" disabled={submitting}>
          {submitting ? "Registrando..." : esPorCobrar ? "Registrar compromiso" : "Registrar pago"}
        </button>
      </div>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════
// MODO: APLICAR crédito a nuevas matrículas
// ══════════════════════════════════════════════════════════════
function AplicarCredito({ pagoEmpresaId, onClose, onSuccess }) {
  const [registro, setRegistro] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [printLoading, setPrintLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    pagoEmpresaService.obtenerPorId(pagoEmpresaId)
      .then((data) => setRegistro(data))
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar el pago empresarial" }))
      .finally(() => setCargando(false));
  }, [pagoEmpresaId]);

  useEffect(() => {
    if (query.trim().length < 2) { setResultados([]); return; }
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const data = await buscarMatriculas({ search: query, estado_pago: "pendiente,parcial", limit: 30 });
        setResultados(Array.isArray(data) ? data : (data?.items || []));
      } catch { setResultados([]); }
      finally { setBuscando(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const toggleMatricula = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    const allIds = resultados.map((m) => m.id);
    const todos = allIds.every((id) => seleccionados.has(id));
    setSeleccionados(todos ? new Set() : new Set(allIds));
  };

  const sumaSeleccionados = resultados
    .filter((m) => seleccionados.has(m.id))
    .reduce((acc, m) => acc + Number(m.saldo || 0), 0);

  const saldoFavor = Number(registro?.saldo_favor || 0);

  const handleAplicar = async () => {
    if (seleccionados.size === 0) return showToast({ type: "warning", message: "Selecciona al menos una matrícula" });
    setSubmitting(true);
    try {
      const res = await pagoEmpresaService.aplicarCredito(pagoEmpresaId, [...seleccionados]);
      showToast({
        type: "success",
        message: `Crédito aplicado a ${res.pagos?.length || 0} matrícula${res.pagos?.length !== 1 ? "s" : ""}. Saldo restante: ${currency(res.saldo_favor_restante)}`
      });
      onSuccess?.(res);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err?.response?.data?.message || err.message || "Error al aplicar crédito" });
    } finally {
      setSubmitting(false);
    }
  };

  if (cargando) {
    return <div className="modal-body text-center py-5"><div className="spinner-border spinner-border-sm text-primary" /></div>;
  }

  if (!registro) {
    return <div className="modal-body"><p className="text-danger">No se encontró el pago empresarial.</p></div>;
  }

  return (
    <>
      <div className="modal-body pt-3">
        {/* Resumen del pago empresarial */}
        <div className="management-card mb-3 p-3">
          <div className="row g-2 text-center">
            <div className="col-4">
              <p className="management-eyebrow mb-0">Empresa</p>
              <p className="fw-semibold small mb-0">{registro.empresa_nombre}</p>
            </div>
            <div className="col-4">
              <p className="management-eyebrow mb-0">Pagado originalmente</p>
              <p className="fw-bold mb-0">{currency(registro.monto_total)}</p>
            </div>
            <div className="col-4">
              <p className="management-eyebrow mb-0">Saldo a favor</p>
              <p className={`fw-bold mb-0 ${saldoFavor > 0 ? "text-success" : "text-muted"}`}>
                {currency(saldoFavor)}
              </p>
            </div>
          </div>
          {registro.referencia_externa && (
            <p className="text-muted small text-center mb-0 mt-2">Ref: {registro.referencia_externa}</p>
          )}
        </div>

        {saldoFavor <= 0 ? (
          <div className="alert alert-warning mb-0">
            Este pago ya no tiene saldo a favor disponible.
          </div>
        ) : (
          <>
            <p className="management-eyebrow mb-2">Seleccionar matrículas a cubrir</p>
            {sumaSeleccionados > saldoFavor && (
              <div className="alert alert-warning py-2 small mb-2">
                La suma seleccionada ({currency(sumaSeleccionados)}) supera el saldo a favor ({currency(saldoFavor)}).
                Se aplicará el saldo hasta agotarse y el resto quedará pendiente en cada matrícula.
              </div>
            )}
            <MatriculasPicker
              seleccionados={seleccionados}
              onToggle={toggleMatricula}
              onToggleAll={toggleAll}
              resultados={resultados}
              buscando={buscando}
              query={query}
              onQuery={setQuery}
            />
          </>
        )}
      </div>
      <div className="modal-footer border-0">
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={printLoading}
          onClick={async () => {
            setPrintLoading(true);
            try {
              const data = await pagoEmpresaService.obtenerComprobante(pagoEmpresaId);
              printHtml(buildCompanyPrintHtml(data, "80mm"));
            } catch { showToast({ type: "danger", message: "No se pudo obtener el comprobante" }); }
            finally { setPrintLoading(false); }
          }}
        >
          <i className="bi bi-printer me-1" />
          {printLoading ? "Generando..." : "Imprimir comprobante"}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cerrar
        </button>
        {saldoFavor > 0 && (
          <button
            type="button"
            className="btn management-btn-primary"
            onClick={handleAplicar}
            disabled={submitting || seleccionados.size === 0}
          >
            {submitting ? "Aplicando..." : `Aplicar crédito a ${seleccionados.size || 0} matrícula${seleccionados.size !== 1 ? "s" : ""}`}
          </button>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// MODO: CONFIRMAR pago por_cobrar
// ══════════════════════════════════════════════════════════════
function ConfirmarPago({ pagoEmpresaId, onClose, onSuccess }) {
  const [registro, setRegistro] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [monto, setMonto] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    pagoEmpresaService.obtenerPorId(pagoEmpresaId)
      .then((data) => { setRegistro(data); setMonto(String(data.monto_total || "")); })
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar el registro" }))
      .finally(() => setCargando(false));
  }, [pagoEmpresaId]);

  const handleConfirmar = async () => {
    if (!monto || Number(monto) <= 0) return showToast({ type: "warning", message: "Ingresa un monto válido" });
    setSubmitting(true);
    try {
      const res = await pagoEmpresaService.confirmar(pagoEmpresaId, Number(monto));
      showToast({ type: "success", message: `Pago confirmado. ${res.pagos?.length || 0} matrícula(s) cubiertas.` });
      onSuccess?.(res);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err?.response?.data?.message || err.message || "Error al confirmar pago" });
    } finally {
      setSubmitting(false);
    }
  };

  if (cargando) {
    return <div className="modal-body text-center py-5"><div className="spinner-border spinner-border-sm text-primary" /></div>;
  }
  if (!registro) {
    return <div className="modal-body"><p className="text-danger">No se encontró el registro.</p></div>;
  }

  return (
    <>
      <div className="modal-body pt-3">
        <div className="management-card mb-3 p-3">
          <div className="row g-2 text-center">
            <div className="col-6">
              <p className="management-eyebrow mb-0">Empresa</p>
              <p className="fw-semibold small mb-0">{registro.empresa_nombre}</p>
            </div>
            <div className="col-6">
              <p className="management-eyebrow mb-0">Matrículas vinculadas</p>
              <p className="fw-bold mb-0">{registro.pagos?.length ?? "—"}</p>
            </div>
          </div>
          {registro.referencia_externa && (
            <p className="text-muted small text-center mb-0 mt-2">Ref: {registro.referencia_externa}</p>
          )}
        </div>
        <div className="alert alert-warning py-2 small mb-3">
          Confirma que la empresa ya realizó el pago. El crédito se aplicará automáticamente a las matrículas vinculadas.
        </div>
        <div className="mb-0">
          <label className="form-label small fw-semibold">Monto recibido *</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className="form-control"
              min="0.01"
              step="0.01"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-text">Monto acordado originalmente: {currency(registro.monto_total)}</div>
        </div>
      </div>
      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </button>
        <button type="button" className="btn management-btn-primary" onClick={handleConfirmar} disabled={submitting}>
          {submitting ? "Confirmando..." : "Confirmar pago recibido"}
        </button>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// EXPORT PRINCIPAL
// mode: "crear" | "aplicar" | "confirmar"
// pagoEmpresaId: requerido en mode="aplicar" o "confirmar"
// ══════════════════════════════════════════════════════════════
export default function PagoEmpresaModal({ open, mode = "crear", pagoEmpresaId, onClose, onSuccess }) {
  const titles = {
    crear: "Pago grupal empresarial",
    aplicar: "Aplicar saldo a favor",
    confirmar: "Confirmar pago por cobrar",
  };
  const subtitles = {
    crear: "Registra el pago de una empresa por múltiples estudiantes",
    aplicar: "Aplica el crédito pendiente a nuevas matrículas",
    confirmar: "Registra el dinero recibido y aplica el crédito a las matrículas",
  };

  return (
    <ModalWrapper open={open} title={titles[mode] || titles.crear} subtitle={subtitles[mode] || ""} onClose={onClose}>
      {mode === "crear" && <CrearPagoEmpresa onClose={onClose} onSuccess={onSuccess} />}
      {mode === "aplicar" && pagoEmpresaId && (
        <AplicarCredito pagoEmpresaId={pagoEmpresaId} onClose={onClose} onSuccess={onSuccess} />
      )}
      {mode === "confirmar" && pagoEmpresaId && (
        <ConfirmarPago pagoEmpresaId={pagoEmpresaId} onClose={onClose} onSuccess={onSuccess} />
      )}
    </ModalWrapper>
  );
}
